/**
 * 🎛️ LES SIX FILTRES DE LA LISTE — et pas un de plus.
 *
 * Kayla, 12-09-2026, la modale de filtres sous les yeux : « il est trop grand,
 * il y a trop de choses. Le seul truc que tu peux mettre, c'est : Telegram
 * seulement, Twitter seulement, non lus seulement, afficher les time wasters,
 * épinglées seulement, et les payeurs. […] Tous les autres, ils servent à
 * rien, personne ne les utilise. » Et : « pas besoin de faire une big modale
 * comme ça, un truc plus minimaliste, plus simple. »
 *
 * 🪦 Sont partis ce jour-là : créateurs (inclure / exclure, avec toute la
 * mécanique de réconciliation de la roster — Kayli invisible le 28-08), type
 * de fan, langues, tags, fenêtre d'arrivée, « Cyrano en pause », « voir toutes
 * les conversations » (les injoignables), « afficher les écartées ». Chacun
 * avait sa raison d'être ; aucun n'était utilisé. Une case de moins est un
 * filtre par défaut invisible de moins (cf. le skill `ecran`).
 *
 * ⛔ Le canal reste UN état à trois valeurs (`telegram` · `twitter` · `tous`),
 * rendu par deux lignes qui s'excluent : « Telegram seulement » (le défaut) et
 * « Twitter seulement ». Décocher les deux montre tout. Deux booléens auraient
 * laissé exister « les deux cochés », qui n'a aucune traduction serveur.
 *
 * Tout se fait côté SERVEUR (`canaux=`, `pinned=`, `include_time_wasters=`,
 * `payeurs=`) : la liste est paginée, filtrer la seule page chargée rendrait
 * une liste fausse alors que la réponse est dix pages plus loin. Les non-lus
 * restent côté client (le compteur vit dans le cockpit, cf. `unread.ts`).
 *
 * 🪶 Un MENU ancré sous l'icône, plus une modale. La modale du 09-09
 * répondait à un popover de neuf sections qui débordait du panneau ; six
 * lignes tiennent dans 240 px.
 *
 * 🎨 Vague 2 (02-11-2026) : le bouton, son compteur et le volet sont `Saisie.Filtres` de la librairie (le `Volet` :
 * porté sur <body>, Échap, clic dehors, le focus qui revient au bouton). Ce fichier garde l'ÉTAT des filtres, leur
 * compte, leur description en clair et les CASES que le volet contient (`SectionsDesFiltres`).
 */
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { Checkbox, Saisie } from "@/shared/ui";

/** 🐦 Quels canaux la liste montre. Le défaut excluait Twitter (Kayla, 04-09) ;
 *  depuis que les chatteurs vendent sur les DM X (étape 0 du 28-10, « corrige » du
 *  29-10), le défaut est « tous » : un fan X qui écrit doit être vu. L'audit du 29-10
 *  comptait 4 164 chargements de liste sur 5 310 avec `canaux=telegram`, et 0 réponse
 *  de chatteur sur X. « Telegram seulement » reste un filtre, nommé et compté. */
export type CanalFiltre = "telegram" | "tous" | "twitter";
export const CANAL_FILTRE_DEFAUT: CanalFiltre = "tous";

export interface FiltersState {
  canalFiltre: CanalFiltre;
  /** Quand true, n'affiche que les conversations avec ≥ 1 message non lu. */
  unreadOnly: boolean;
  /** Quand true, réaffiche les fans tagués « Time Waster » (masqués côté
   *  serveur par défaut — Kayla, 19-08 : « toujours exclu de base »). */
  showTimeWasters: boolean;
  /** Quand true, n'affiche QUE les épinglées (tag système « PIN »). */
  pinned: boolean;
  /** 💶 Quand true, n'affiche QUE les fans qui ont DÉJÀ payé (au moins une
   *  commande payée ou livrée, montant > 0). Serveur : `payeurs=true`. */
  payeurs: boolean;
}

export const EMPTY_FILTERS: FiltersState = {
  canalFiltre: CANAL_FILTRE_DEFAUT,
  unreadOnly: false,
  showTimeWasters: false,
  pinned: false,
  payeurs: false,
};

/** Un filtre actif, nommé et défaisable TOUT SEUL. */
export interface ActiveFilter {
  key: string;
  label: string;
  /** Rend l'état où ce seul filtre est levé — les autres restent. */
  clear: (f: FiltersState) => FiltersState;
}

/**
 * Les filtres actifs, en clair — pour l'état vide de la liste, qui doit dire
 * LEQUEL l'a vidée (Kayla, 17-08 : « IRL » coché, zéro carte, et pour seule
 * sortie « Effacer les filtres »). Même source que `activeFilterCount` : le
 * badge et cette liste se dérivent l'un de l'autre.
 */
export function describeActiveFilters(f: FiltersState, t: TFunction): ActiveFilter[] {
  const out: ActiveFilter[] = [];
  const push = (key: string, label: string, clear: (s: FiltersState) => FiltersState) =>
    out.push({ key, label, clear });

  if (f.pinned)
    push("pinned", t("panels.conversations.filters.pinned", { defaultValue: "Épinglées seulement" }),
      (s) => ({ ...s, pinned: false }));
  // 🐦 Le défaut (`tous`) ne se nomme PAS : c'est la vue normale. Les deux
  // autres, si — et les lever ramène au défaut.
  if (f.canalFiltre === "twitter")
    push("canalTwitter", t("panels.conversations.filters.twitterOnly", { defaultValue: "Twitter seulement" }),
      (s) => ({ ...s, canalFiltre: CANAL_FILTRE_DEFAUT }));
  if (f.canalFiltre === "telegram")
    push("canalTelegram", t("panels.conversations.filters.telegramOnly", { defaultValue: "Telegram seulement" }),
      (s) => ({ ...s, canalFiltre: CANAL_FILTRE_DEFAUT }));
  if (f.unreadOnly)
    push("unreadOnly", t("panels.conversations.filters.unreadOnly", { defaultValue: "Non lus seulement" }),
      (s) => ({ ...s, unreadOnly: false }));
  if (f.payeurs)
    push("payeurs", t("panels.conversations.filters.payeurs", { defaultValue: "Payeurs seulement" }),
      (s) => ({ ...s, payeurs: false }));
  if (f.showTimeWasters)
    push("showTimeWasters", t("panels.conversations.filters.showTimeWasters", { defaultValue: "Afficher les Time Waster" }),
      (s) => ({ ...s, showTimeWasters: false }));
  return out;
}

/** Combien de filtres dévient du défaut — le badge sur l'icône. */
export function activeFilterCount(f: FiltersState): number {
  let n = 0;
  if (f.pinned) n++;
  if (f.unreadOnly) n++;
  if (f.payeurs) n++;
  // Réafficher les Time Waster est une déviation du défaut (= exclus).
  if (f.showTimeWasters) n++;
  // 🐦 Le défaut montre tout : « Twitter seulement » ET « Telegram seulement »
  // sont deux déviations, et le badge doit dire les deux — un filtre par défaut
  // invisible est exactement ce que ce compteur existe pour éviter.
  if (f.canalFiltre !== CANAL_FILTRE_DEFAUT) n++;
  return n;
}

/**
 * Le contenu du volet des filtres : une section, cinq cases (une coche à gauche de celles qui sont armées).
 * « Telegram seulement » et « Twitter seulement » s'excluent — un seul état à trois valeurs. Le bouton, son
 * compteur, le volet et « Effacer les filtres » au pied sont `Saisie.Filtres` ; l'écran lui passe ces cases.
 */
export function SectionsDesFiltres({
  filters,
  onChange,
}: {
  filters: FiltersState;
  onChange: (next: FiltersState) => void;
}) {
  const { t } = useTranslation();
  return (
    <Saisie.SectionDeFiltres titre={t("panels.conversations.filters.title", { defaultValue: "Filtres" })}>
      <Checkbox
        label={t("panels.conversations.filters.telegramOnly", { defaultValue: "Telegram seulement" })}
        checked={filters.canalFiltre === "telegram"}
        onChange={() =>
          onChange({ ...filters, canalFiltre: filters.canalFiltre === "telegram" ? "tous" : "telegram" })
        }
      />
      <Checkbox
        label={t("panels.conversations.filters.twitterOnly", { defaultValue: "Twitter seulement" })}
        checked={filters.canalFiltre === "twitter"}
        onChange={() =>
          onChange({ ...filters, canalFiltre: filters.canalFiltre === "twitter" ? "tous" : "twitter" })
        }
      />
      <Checkbox
        label={t("panels.conversations.filters.unreadOnly", { defaultValue: "Non lus seulement" })}
        checked={filters.unreadOnly}
        onChange={() => onChange({ ...filters, unreadOnly: !filters.unreadOnly })}
      />
      <Checkbox
        label={t("panels.conversations.filters.pinned", { defaultValue: "Épinglées seulement" })}
        checked={filters.pinned}
        onChange={() => onChange({ ...filters, pinned: !filters.pinned })}
      />
      <Checkbox
        label={t("panels.conversations.filters.payeurs", { defaultValue: "Payeurs seulement" })}
        checked={filters.payeurs}
        onChange={() => onChange({ ...filters, payeurs: !filters.payeurs })}
      />
    </Saisie.SectionDeFiltres>
  );
}
